import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

/**
 * Real dashboard data from TestSuite AI's own backend (/api/v1/dashboard/overview),
 * which aggregates BRDs, generated test cases, automation scripts and Playwright
 * runs out of Mongo.
 *
 * This is deliberately separate from CoverageProvider: that one feeds the
 * requirement-coverage widgets from the standalone coverage API, while this one
 * feeds the sections that TestSuite AI itself owns — test cases, automation
 * scripts and traceability.
 *
 * Widgets read `live` to decide whether they are showing real data. When the
 * request fails they get `live: false` and an empty dataset rather than
 * invented numbers, so a broken backend looks empty instead of looking healthy.
 */

const EMPTY = {
  sources: { brdCount: 0, sections: 0, documents: [] },
  testCases: {
    total: 0,
    positive: 0,
    negative: 0,
    aiGenerated: 0,
    manual: 0,
    approved: 0,
    review: 0,
    sections: 0,
    modules: [],
    items: [],
  },
  automationScripts: {
    total: 0,
    byKind: {},
    runs: { total: 0, passed: 0, failed: 0, notRun: 0, passRate: 0 },
    items: [],
  },
  traceability: {
    overallPct: 0,
    totalScripts: 0,
    fullyTraced: 0,
    partial: 0,
    notTraced: 0,
    documents: [],
  },
  sectionCoverage: {
    totalSections: 0,
    coveragePct: 0,
    breakdown: { Covered: 0, Partial: 0, Missing: 0 },
    slices: [],
    documents: [],
    gaps: [],
  },
  flaky: { flakyTests: 0, testsWithRuns: 0, totalRuns: 0, rate: 0, items: [] },
  criticalIssues: { total: 0, runFailures: 0, generationFailures: 0, items: [] },
  moduleDefects: { totalDefects: 0, modules: [] },
  inputSources: { totalArtifacts: 0, tiles: [] },
  pipeline: { totalSections: 0, stages: [], releaseReadinessPct: 0 },
  costBasis: {
    testCases: 0, sections: 0, automationScripts: 0, automationRuns: 0,
    generations: 0, llmCalls: 0, llmApiMs: 0, models: [],
  },
  aiHealth: {
    generations: 0,
    succeeded: 0,
    failed: 0,
    successRate: 0,
    scriptGenerationRate: 0,
    scriptedSections: 0,
    sectionsWithCases: 0,
    automationRate: 0,
    automatedCases: 0,
    avgGenerationMs: 0,
    timingSamples: 0,
    avgLlmCalls: 0,
    llmCalls: 0,
    llmApiMs: 0,
    llmFailures: 0,
    openGaps: 0,
    models: [],
  },
};

/**
 * Merge the response onto EMPTY one level deep.
 *
 * A shallow spread would let an older backend replace a whole section (say
 * `aiHealth`) with one missing newer keys, and the first `.toLocaleString()`
 * on the resulting `undefined` white-screens the dashboard. Front-end and
 * back-end get deployed independently, so tolerate the skew: unknown-to-the-API
 * fields keep their zero defaults instead of vanishing.
 */
function withDefaults(payload) {
  const merged = { ...EMPTY, ...payload };
  for (const [key, fallback] of Object.entries(EMPTY)) {
    const incoming = payload?.[key];
    if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) {
      merged[key] = { ...fallback, ...(incoming && typeof incoming === 'object' ? incoming : {}) };
    }
  }
  return merged;
}

const TestSuiteDataContext = createContext(null);

/** Time windows offered by the top-bar date control. `null` days = all time. */
export const TIME_RANGES = [
  { id: 'all', label: 'All time', days: null },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
  { id: '365d', label: 'Last 12 months', days: 365 },
];

const DEFAULT_FILTERS = { brdId: 'all', module: 'all', range: 'all' };

const withinRange = (timestamp, days) => {
  if (!days) return true;
  const value = Date.parse(timestamp || '');
  if (Number.isNaN(value)) return true; // never hide records that lack a date
  return value >= Date.now() - days * 24 * 60 * 60 * 1000;
};

/**
 * Re-derives every aggregate from the filtered rows.
 *
 * The API returns the full set once; narrowing by BRD, module or time happens
 * here so the top-bar controls feel instant and stay consistent — the KPI
 * numbers, the drawers and the traceability tree all read the same filtered
 * result rather than each filtering independently.
 */
function applyFilters(data, filters) {
  const { brdId, module, range } = filters;
  const days = TIME_RANGES.find((r) => r.id === range)?.days ?? null;
  const unfiltered = brdId === 'all' && module === 'all' && !days;
  if (unfiltered) return data;

  const keepCase = (t) =>
    (brdId === 'all' || t.brdId === brdId) &&
    (module === 'all' || t.module === module) &&
    withinRange(t.updatedAt || t.createdAt, days);

  const keepScript = (s) =>
    (brdId === 'all' || s.brdId === brdId) &&
    (module === 'all' || s.suite === module || s.sectionTitle === module) &&
    withinRange(s.lastRunAt || s.updatedAt || s.createdAt, days);

  const cases = data.testCases.items.filter(keepCase);
  const scripts = data.automationScripts.items.filter(keepScript);

  const moduleCounts = new Map();
  let positive = 0;
  let negative = 0;
  let ai = 0;
  let manual = 0;
  let approved = 0;
  let review = 0;
  const sections = new Set();

  for (const t of cases) {
    if (t.type === 'Negative') negative += 1;
    else positive += 1;
    if (t.source === 'Manual') manual += 1;
    else ai += 1;
    if (t.status === 'Approved') approved += 1;
    else if (t.status === 'Review') review += 1;
    sections.add(`${t.brdId}#${t.sectionNumber}`);
    moduleCounts.set(t.module, (moduleCounts.get(t.module) || 0) + 1);
  }

  const runsTotal = scripts.reduce((n, s) => n + (s.runCount || 0), 0);
  const runsPassed = scripts.reduce((n, s) => n + (s.runsPassed || 0), 0);
  const runsFailed = scripts.reduce((n, s) => n + (s.runsFailed || 0), 0);

  // Traceability follows the same rows, so the tree can never disagree
  // with the KPI cards above it.
  const caseKeys = new Set(cases.map((t) => `${t.brdId}#${t.sectionNumber}`));
  const scriptIds = new Set(scripts.map((s) => s.id));
  const documents = data.traceability.documents
    .filter((doc) => brdId === 'all' || doc.id === brdId)
    .map((doc) => {
      const scriptItems = doc.scriptItems.filter((s) => scriptIds.has(s.id));
      const docSections = new Set(
        [...caseKeys].filter((k) => k.startsWith(`${doc.id}#`)).map((k) => k.split('#')[1])
      );
      for (const s of scriptItems) docSections.add(String(s.sectionNumber));
      const tracedSections = new Set(scriptItems.map((s) => String(s.sectionNumber)));
      const sectionCount = docSections.size;
      const coveragePct = sectionCount
        ? Math.round((tracedSections.size / sectionCount) * 100)
        : 0;
      const status = !sectionCount || !tracedSections.size
        ? 'Not Traced'
        : tracedSections.size === sectionCount
          ? 'Fully Traced'
          : 'Partial';
      return {
        ...doc,
        scripts: scriptItems.length,
        tests: cases.filter((t) => t.brdId === doc.id).length,
        sections: sectionCount,
        coveragePct,
        status,
        scriptItems: scriptItems.map((s) => ({
          ...s,
          mappedCases: s.mappedCases.filter(
            (c) => module === 'all' || cases.some((t) => t.id === c.id)
          ),
        })),
      };
    })
    .filter((doc) => doc.sections > 0 || brdId !== 'all');

  const totalSections = documents.reduce((n, d) => n + d.sections, 0);
  const tracedSections = documents.reduce(
    (n, d) => n + new Set(d.scriptItems.map((s) => String(s.sectionNumber))).size,
    0
  );

  // Section coverage narrows by BRD; the breakdown is re-derived from the
  // surviving documents so the donut always matches the rows beneath it.
  const coverageDocs = data.sectionCoverage.documents.filter(
    (d) => brdId === 'all' || d.id === brdId
  );
  const cov = coverageDocs.reduce(
    (acc, d) => ({
      Covered: acc.Covered + d.covered,
      Partial: acc.Partial + d.partial,
      Missing: acc.Missing + d.missing,
    }),
    { Covered: 0, Partial: 0, Missing: 0 }
  );
  const covTotal = cov.Covered + cov.Partial + cov.Missing || 1;
  const covPct = (n) => Math.round((n / covTotal) * 100);

  const selectedBrdName = data.traceability.documents.find((d) => d.id === brdId)?.name;

  return {
    ...data,
    sectionCoverage: {
      ...data.sectionCoverage,
      totalSections: cov.Covered + cov.Partial + cov.Missing,
      coveragePct: covPct(cov.Covered),
      breakdown: cov,
      slices: [
        { label: 'Covered', status: 'Covered', count: cov.Covered, pct: covPct(cov.Covered) },
        { label: 'Partially Covered', status: 'Partial', count: cov.Partial, pct: covPct(cov.Partial) },
        { label: 'Missing', status: 'Missing', count: cov.Missing, pct: covPct(cov.Missing) },
      ],
      documents: coverageDocs,
      gaps: data.sectionCoverage.gaps.filter(
        (g) =>
          (brdId === 'all' || g.brdId === brdId) &&
          (module === 'all' || g.sectionTitle === module)
      ),
    },
    flaky: {
      ...data.flaky,
      items: data.flaky.items.filter((i) => brdId === 'all' || i.brdId === brdId),
    },
    criticalIssues: {
      ...data.criticalIssues,
      items: data.criticalIssues.items.filter(
        (i) => brdId === 'all' || !selectedBrdName || i.brds.includes(selectedBrdName)
      ),
    },
    moduleDefects: {
      ...data.moduleDefects,
      modules: data.moduleDefects.modules.filter((m) => module === 'all' || m.name === module),
    },
    testCases: {
      ...data.testCases,
      total: cases.length,
      positive,
      negative,
      aiGenerated: ai,
      manual,
      approved,
      review,
      sections: sections.size,
      modules: [...moduleCounts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      items: cases,
    },
    automationScripts: {
      ...data.automationScripts,
      total: scripts.length,
      runs: {
        total: runsTotal,
        passed: runsPassed,
        failed: runsFailed,
        notRun: scripts.filter((s) => s.status === 'not_run').length,
        passRate: runsTotal ? Math.round((runsPassed / runsTotal) * 100) : 0,
      },
      items: scripts,
    },
    traceability: {
      ...data.traceability,
      overallPct: totalSections ? Math.round((tracedSections / totalSections) * 100) : 0,
      totalScripts: scripts.length,
      fullyTraced: documents.filter((d) => d.status === 'Fully Traced').length,
      partial: documents.filter((d) => d.status === 'Partial').length,
      notTraced: documents.filter((d) => d.status === 'Not Traced').length,
      documents,
    },
  };
}

export function TestSuiteDataProvider({ children }) {
  const [state, setState] = useState({
    live: false,
    loading: true,
    error: null,
    data: EMPTY,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const response = await apiFetch('/api/v1/dashboard/overview');
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? 'Sign in to load dashboard data.'
            : `Dashboard API returned ${response.status}`
        );
      }
      const payload = await response.json();
      setState({
        live: true,
        loading: false,
        error: null,
        data: withDefaults(payload),
      });
    } catch (err) {
      setState({
        live: false,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        data: EMPTY,
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const setFilter = useCallback(
    (key, value) => setFilters((f) => ({ ...f, [key]: value })),
    []
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const filtered = useMemo(() => applyFilters(state.data, filters), [state.data, filters]);

  // Options always come from the unfiltered set, so narrowing to one BRD never
  // removes the choices you need to get back out.
  const options = useMemo(() => {
    const brds = new Map();
    for (const doc of state.data.traceability.documents) brds.set(doc.id, doc.name);
    for (const t of state.data.testCases.items) if (!brds.has(t.brdId)) brds.set(t.brdId, t.brdName);
    const modules = [...new Set(state.data.testCases.items.map((t) => t.module))].sort();
    return {
      brds: [{ id: 'all', name: 'All BRDs' }, ...[...brds].map(([id, name]) => ({ id, name }))],
      modules: ['all', ...modules],
      ranges: TIME_RANGES,
    };
  }, [state.data]);

  const value = useMemo(
    () => ({
      ...state,
      ...filtered,
      unfilteredTotals: {
        testCases: state.data.testCases.total,
        scripts: state.data.automationScripts.total,
      },
      filters,
      setFilter,
      resetFilters,
      options,
      refresh: () => void load(),
    }),
    [state, filtered, filters, setFilter, resetFilters, options, load]
  );

  return (
    <TestSuiteDataContext.Provider value={value}>{children}</TestSuiteDataContext.Provider>
  );
}

export function useTestSuiteData() {
  const ctx = useContext(TestSuiteDataContext);
  if (!ctx) throw new Error('useTestSuiteData must be used inside <TestSuiteDataProvider>');
  return ctx;
}
